import './EditTheme.scss';

import { ChangeEvent, FC, FocusEvent, useContext, useEffect, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useSingleEntity } from 'customHooks/useSingleEntity';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { isEmptyObject } from 'functions/isEmptyObject';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Theme } from 'models/Theme';
import moment from 'moment';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditThemeProps {}

const EditTheme: FC<EditThemeProps> = () => {
  const newEntryId = 'new';
  const navigate = useNavigate();
  const { id } = useParams();
  const themeId: number | undefined = id !== newEntryId ? parseInt(id!, 10) : undefined;
  const {
    configRepo,
    setIsProjectSelectable,
    selectedProjectId,
    notify,
    sendLog
  } = useContext(AppContext);
  const now = new Date();
  const defaultDate = new Date(now.getFullYear(), now.getMonth()+1, 1);
  const defaultTheme: Theme = {
    id: 0,
    name: '',
    description: '',
    scheduledDate: defaultDate,
    projectId: selectedProjectId!
  };
  const [cachedTheme, setCachedTheme] = useState<Theme>(defaultTheme);
  const onTokenExpired = useTokenExpiredHandler();
  const { createEntity: createTheme, updateEntity: updateTheme } =
    useModifyEntityService<Theme>(
      configRepo!.SCHEDULE_API_BASE_URI,
      Entities.Themes,
      sendLog,
      onTokenExpired,
    );

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  const { entityRequest: themeRequest } = useSingleEntity<Theme>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Themes,
    themeId,
  );
  const loadedTheme: Theme = themeRequest.entity;

  useEffect(() => {
    const updatedCachedTheme = { ...loadedTheme };
    if (isEmptyObject(updatedCachedTheme)) {
      return;
    }
    setCachedTheme(updatedCachedTheme);
  }, [loadedTheme]);

  const onDocumentFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedCachedTheme = { ...cachedTheme };

    if (event.target.type === 'date') {
      const selectedDate = event.target.valueAsDate!;
      if (!selectedDate) return;
      (updatedCachedTheme as any)[event.target.name] = new Date(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
      );
    } else {
      (updatedCachedTheme as any)[event.target.name] = event.target.value;
    }

    setCachedTheme(updatedCachedTheme);
  };

  const logAndNotifyThemeSaved = (updatedTheme: Theme) => {
    sendLog(`Theme has been updated successfully, ${updatedTheme.id}`, LogLevel.info);
    notify('Theme has been updated successfully.', NotificationType.Success);
  };

  const logAndNotifyThemeSaveFailure = (themeForUpdate: Theme) => {
    notify('Theme has NOT been updated successfully.', NotificationType.Error);
    sendLog(`Theme has NOT been updated successfully, ${themeForUpdate.id}`, LogLevel.error, { themeId : themeForUpdate.id });
  };

  /* eslint-disable no-param-reassign */
  const ensureDateIsValid = (event: FocusEvent<HTMLInputElement>) => {
    const enteredDate = new Date(event.target.value);
    const minDate = new Date(event.target.min);
    const maxDate = new Date(event.target.max);

    if (enteredDate < minDate) {
      event.target.value = event.target.min;
      notify('When modifying the planned date it must be in the future.', NotificationType.Warning);
    } else if (enteredDate > maxDate) {
      event.target.value = event.target.max;
      notify(
        'When modifying the planned date it must be within the next year.',
        NotificationType.Warning,
      );
    }
  };

  const onSave = async (): Promise<{ hasSavedSuccessfully: boolean, theme: Theme}> => {
    let updatedTheme = { ...cachedTheme };
    let hasSavedSuccessfully = false;

    if (id === newEntryId) {     
      const result = await createTheme(updatedTheme);
      if (result) {
        setCachedTheme(result);
        updatedTheme = result; // have to update current context since that's used downstream from here
        navigate(window.location.pathname.replace(newEntryId, result.id!.toString()), {
          replace: true,
        });
        hasSavedSuccessfully = true;
      } else {
        hasSavedSuccessfully = false;
      }
    } else {
      hasSavedSuccessfully = await updateTheme(updatedTheme);
    }

    return { hasSavedSuccessfully, theme : updatedTheme };
  };

  const heading: NavHeadingProps = {
    title: id === newEntryId ? 'Create New Theme' : 'Edit Theme',
    ...NavHeadings.Themes,
  };
  
  return (
  <div className="EditTheme">
    <NavHeading {...heading} />
    <Row>
      <Form.Group className="col-md-12">
        <Form.Label>Theme name</Form.Label>
        <Form.Control
          type="text"
          value={cachedTheme.name}
          onChange={onDocumentFieldChange}
          aria-label="name"
          name="name"
          placeholder="Theme Name"
          className="mb-2"
          tabIndex={1}
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group className="col-md-12">
      <Form.Label>Description</Form.Label>
      <Form.Control
          type="text"
          value={cachedTheme.description}
          onChange={onDocumentFieldChange}
          aria-label="Description"
          name="description"
          placeholder="Enter short description here..."
          className="mb-2"
          tabIndex={2}
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group className="col-md-12">
        <Form.Label>Date</Form.Label>
        <Form.Control
          type="date"
          value={
            cachedTheme.scheduledDate
              ? moment(cachedTheme.scheduledDate).format('YYYY-MM-DD')
              : moment(new Date()).add(1, 'months').format('YYYY-MM-DD')
          }
          min={moment(new Date()).format('YYYY-MM-DD')}
          max={moment(new Date()).add(1, 'years').format('YYYY-MM-DD')}
          pattern="\d{4}-\d{2}-\d{2}"
          onChange={onDocumentFieldChange}
          onBlur={ensureDateIsValid}
          aria-label="planned date"
          name="scheduledDate"
          tabIndex={3}
          required
        />
      </Form.Group>
    </Row>
    
    <Row className="fixed-bottom approval-bar">
      <Col md={12} className="btn-sticky-top pull-right">
        <div className="action-buttons">
          <Link
            to="/themes"
            className="btn btn-light mb-2 mx-2"
            title="Return to themes list">
              Cancel
          </Link>
          <Button
            type="button"
            onClick={async () => { 
              const result = await onSave();
              if (result.hasSavedSuccessfully) {
                logAndNotifyThemeSaved(result.theme);
                setCachedTheme(result.theme);
              } else {
                logAndNotifyThemeSaveFailure(result.theme);
              }
            }}
            className=" btn btn-success mb-2 mx-2"
            tabIndex={7}
            title='Save connection information'
          >
            <i className="bi bi-journal-check px-2 xxs-hide" />
            Save
          </Button>
        </div>
      </Col>
    </Row>

  </div>
  );
}

export default EditTheme;
